import { Add, Delete, Edit } from "@mui/icons-material"
import { Stack } from "@mui/material"
import type { GridColDef } from "@mui/x-data-grid-pro"
import { GridActionsCellItem } from "@mui/x-data-grid-pro"
import type { BiomeshUnitResponse } from "@phc/common/gen/threatAnalyticsGen"
import { useListUnitsBiomeshUnitListGet } from "@phc/common/gen/threatAnalyticsGen"
import type { FC } from "react"
import { useCallback, useMemo, useState } from "react"
import { BaseGrid } from "../BaseGrid/BaseGrid"
import Button from "../Shared/Button"
import { branchTypeLabels, unitTypeLabels } from "./copUtils"
import { DeleteUnitDialog } from "./DeleteUnitDialog"
import { UnitFormDialog } from "./UnitFormDialog"

export const UnitList: FC = () => {
  const { data: units, isLoading } = useListUnitsBiomeshUnitListGet({
    include: "operations,contacts",
  })

  const [formOpen, setFormOpen] = useState(false)
  const [editUnit, setEditUnit] = useState<BiomeshUnitResponse | undefined>()
  const [deleteUnit, setDeleteUnit] = useState<
    BiomeshUnitResponse | undefined
  >()

  const handleAdd = useCallback(() => {
    setEditUnit(undefined)
    setFormOpen(true)
  }, [])

  const handleEdit = useCallback((unit: BiomeshUnitResponse) => {
    setEditUnit(unit)
    setFormOpen(true)
  }, [])

  const handleCloseForm = useCallback(() => {
    setFormOpen(false)
    setEditUnit(undefined)
  }, [])

  const columns: GridColDef<BiomeshUnitResponse>[] = useMemo(
    () => [
      { field: "name", headerName: "Name", flex: 1 },
      {
        field: "unit_type",
        headerName: "Unit Type",
        flex: 0.8,
        valueFormatter: (value: string) =>
          (unitTypeLabels as Record<string, string>)[value] ?? value,
      },
      {
        field: "branch_type",
        headerName: "Branch",
        flex: 0.8,
        valueFormatter: (value: string) =>
          (branchTypeLabels as Record<string, string>)[value] ?? value,
      },
      { field: "uic", headerName: "UIC", flex: 0.6 },
      {
        field: "operations",
        headerName: "Operations",
        flex: 1,
        valueGetter: (_value, row) =>
          row.operations?.map(o => o.name).join(", ") ?? "",
      },
      {
        field: "contacts",
        headerName: "Contacts",
        flex: 1,
        valueGetter: (_value, row) =>
          row.contacts?.map(c => `${c.first_name} ${c.last_name}`).join(", ") ??
          "",
      },
      {
        field: "created_at",
        headerName: "Created At",
        flex: 0.8,
        valueFormatter: (value: string | null) =>
          value ? new Date(value).toLocaleDateString() : "—",
      },
      {
        field: "actions",
        type: "actions",
        headerName: "Actions",
        width: 100,
        disableExport: true,
        getActions: ({ row }) => [
          <GridActionsCellItem
            key="edit"
            icon={<Edit />}
            label="Edit"
            onClick={() => handleEdit(row)}
          />,
          <GridActionsCellItem
            key="delete"
            icon={<Delete />}
            label="Delete"
            onClick={() => setDeleteUnit(row)}
          />,
        ],
      },
    ],
    [handleEdit]
  )

  return (
    <>
      <Stack spacing={2}>
        <Stack
          direction="row"
          sx={{
            justifyContent: "flex-end",
          }}
        >
          <Button variant="contained" startIcon={<Add />} onClick={handleAdd}>
            Add Unit
          </Button>
        </Stack>
        <BaseGrid
          rows={units ?? []}
          columns={columns}
          getRowId={row => row.unit_id}
          loading={isLoading}
          search
          showToolbar
          exported
          exportFileName="biomesh-units"
          placeholder="Search units..."
        />
      </Stack>
      <UnitFormDialog
        key={editUnit?.unit_id ?? "new"}
        open={formOpen}
        onClose={handleCloseForm}
        unit={editUnit}
      />
      {deleteUnit && (
        <DeleteUnitDialog
          open
          onClose={() => setDeleteUnit(undefined)}
          unit={deleteUnit}
        />
      )}
    </>
  )
}
